import {
  TerminWochentage,
  WOCHENTAGE,
} from '@/domain/termine/shared/types/TerminWochentage';
import { Toggle } from '@e2n/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { getFixedWeekday } from '@/domain/termine/shared/utils/getWeekdaySelection';
import dayjs from 'dayjs';

type WochentageProps = {
  date: dayjs.Dayjs;
  value: TerminWochentage[];
  onChange: (next: TerminWochentage[]) => void;
};

const toUniqueWeekdays = (days: TerminWochentage[]) => [...new Set(days)];

export function WeekdayToggle({ value, onChange, date }: WochentageProps) {
  const { t } = useTranslation();
  const fixedWeekday = getFixedWeekday(date);
  const selected = toUniqueWeekdays([fixedWeekday, ...value]);

  const weekdayOptions = useMemo(
    () =>
      WOCHENTAGE.map((weekdayOption) => ({
        value: weekdayOption.value,
        label: t(`general.date.days.${weekdayOption.labelKey}Short`),
      })),
    [t],
  );
  const handleChange = (day: TerminWochentage, pressed: boolean) => {
    if (day === fixedWeekday) return;
    const next = pressed
      ? [...selected, day]
      : selected.filter((selectedDay) => selectedDay !== day);
    onChange(toUniqueWeekdays(next));
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-neutrals-900 text-xs font-bold hidden md:block">
        {t('domain.termine.sheet.fields.amRequired')}
      </span>
      <div className="flex gap-2 justify-evenly md:justify-start">
        {weekdayOptions.map((weekdayOption) => {
          const isBaseDay = weekdayOption.value === fixedWeekday;
          return (
            <Toggle
              key={weekdayOption.value}
              value={weekdayOption.value}
              onPressedChange={(pressed) =>
                handleChange(weekdayOption.value, pressed)
              }
              pressed={selected.includes(weekdayOption.value)}
              disabled={isBaseDay}
              className={cn(
                'size-10 rounded-full cursor-pointer',
                'bg-neutrals-300 text-sm font-normal',
                'data-[state=on]:bg-primary-blue-600 data-[state=on]:text-neutrals-100',
              )}>
              {weekdayOption.label}
            </Toggle>
          );
        })}
      </div>
    </div>
  );
}
